import { useEffect, useId, useState } from "react";
import { MdAdd, MdArrowBack, MdClose, MdDelete, MdEdit, MdHome } from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import Avatar from "components/avatar";
import Badge from "components/badge/Badge";
import Button, { IconButton } from "components/button";
import { CardHeader } from "components/card";
import { useConfirm } from "components/dialog/ConfirmProvider";
import InputField from "components/fields/InputField";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import notify from "lib/notify";
import ImageUploader from "./image";
import HouseholdMultiSelect from "./multiselect";

const HouseHold = ({ user }) => {
  const confirm = useConfirm();
  const joinLabelId = useId();
  const [showAddHousehold, setShowAddHousehold] = useState(false);
  const [showNewHouseholdForm, setShowNewHouseholdForm] = useState(false);
  const [isEditingMember, setIsEditingMember] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [allergyFields, setAllergyFields] = useState([""]);

  const [usersList, setUsersList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [houseHoldName, setHouseHoldName] = useState("");
  const [houseHoldImageFile, setHouseHoldImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [houseHoldsList, setHouseHoldsList] = useState([]);
  const [selectedHousehold, setSelectedHousehold] = useState(null);

  const [houseHoldsLsLoading, setHouseHoldsLsLoading] = useState(true);
  const [houseHoldsError, setHouseHoldsError] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  const getHouseHoldsByUser = async () => {
    setHouseHoldsLsLoading(true);
    setHouseHoldsError(null);
    try {
      const response = await AtriumApi.get(`/v2/households/household/${user?.id}`);
      if (response) {
        setHouseHoldsList(response?.data?.data);
      }
    } catch (error) {
      setHouseHoldsError(error);
    } finally {
      setHouseHoldsLsLoading(false);
    }
  };

  const getUsersByHouseHold = async (id) => {
    setUsersLoading(true);
    try {
      const response = await AtriumApi.get(
        `/v2/households/users/${id}?currentUserId=${user?.id}`
      );
      if (response) {
        setSelectedUsers(
          response?.data?.data?.map((user) => ({
            id: user?.userHouseholdUser?.id,
            name:
              user?.userHouseholdUser?.firstName +
              " " +
              user?.userHouseholdUser?.lastName,
            role: user?.userHouseholdUser?.role,
            avatar: { url: user?.userHouseholdUser?.avatar },
            email: user?.userHouseholdUser?.email,
            phone: user?.userHouseholdUser?.phone,
            isPrimary: user?.isPrimary || false,
            userDetails: {
              relationshipText:
                user?.userHouseholdHousehold?.relationshipText || "",
              schoolGrade: user?.userHouseholdHousehold?.schoolGrade || "",
              allergies: user?.allergies || [],
            },
          }))
        );
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not load household members"));
    } finally {
      setUsersLoading(false);
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setIsEditingMember(true);
  };

  const handleRemoveUser = async (member) => {
    const confirmed = await confirm({
      title: `Remove "${member?.name}"?`,
      message: "They will be removed from this household when you save it.",
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!confirmed) return false;
    setSelectedUsers((prev) => prev?.filter((u) => u?.id !== member?.id));
    notify.success(`${member?.name} removed`);
    return true;
  };

  const handleCancelEdit = () => {
    if (selectedHousehold) {
      setShowAddHousehold(false);
      setShowNewHouseholdForm(false);
      setIsEditingMember(false);
    } else {
      setShowNewHouseholdForm(false);
      setShowAddHousehold(true);
    }
    setSelectedUsers([]);
    setHouseHoldName("");
    setHouseHoldImageFile(null);
    setImagePreview(null);
    setSelectedHousehold(null);
  };

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await AtriumApi.get("/v1/users?page=1&status=active");
        if (response) {
          setUsersList(response?.data?.users);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchAllUsers();
  }, []);

  const handleUserDetailChange = (field, value) => {
    setSelectedMember((prev) => ({
      ...prev,
      userDetails: {
        ...prev.userDetails,
        [field]: value,
      },
    }));
  };

  const handleAddAllergy = () => {
    setSelectedMember((prev) => ({
      ...prev,
      userDetails: {
        ...prev.userDetails,
        allergies: [...prev.userDetails.allergies, ""],
      },
    }));
  };

  const handleAllergyChange = (index, value) => {
    setSelectedMember((prev) => {
      const newAllergies = [...prev.userDetails.allergies];
      newAllergies[index] = value;
      return {
        ...prev,
        userDetails: {
          ...prev.userDetails,
          allergies: newAllergies,
        },
      };
    });
  };

  const handleRemoveAllergy = (index) => {
    setSelectedMember((prev) => ({
      ...prev,
      userDetails: {
        ...prev.userDetails,
        allergies: prev.userDetails.allergies.filter((_, i) => i !== index),
      },
    }));
  };

  const handleSave = () => {
    setSelectedUsers((prevMembers) =>
      prevMembers.map((member) =>
        member.id === selectedMember.id ? selectedMember : member
      )
    );
    setIsEditingMember(false);
  };

  const handleMakePrimary = () => {
    setSelectedUsers((prevMembers) =>
      prevMembers.map((member) => ({
        ...member,
        isPrimary: member.id === selectedMember.id,
      }))
    );
    setIsEditingMember(false);
  };

  useEffect(() => {
    if (user && user.id) {
      getHouseHoldsByUser();
    }
  }, [user?.id]);

  useEffect(() => {
    if (selectedMember) {
      setAllergyFields(selectedMember.userDetails.allergies || []);
    }
  }, [selectedMember]);

  useEffect(() => {
    if (selectedHousehold) {
      setHouseHoldName(selectedHousehold?.name);
      setImagePreview(selectedHousehold?.avatar);
    }
  }, [selectedHousehold]);

  const handleSaveHousehold = (e) => {
    e.preventDefault();

    setLoading(true);

    let requestData;
    let config = {};

    const primaryContact = selectedUsers.find((u) => u.isPrimary);

    // Use FormData only when uploading a new file
    if (houseHoldImageFile) {
      const formData = new FormData();
      formData.append("name", houseHoldName);
      formData.append("avatar", houseHoldImageFile);
      formData.append("userDetails", JSON.stringify(selectedUsers));
      formData.append("primaryContactId", primaryContact?.id || user?.id);
      formData.append(
        "primaryContactName",
        primaryContact?.name || user?.firstName + " " + user?.lastName
      );

      requestData = formData;
      // Don't set Content-Type header - let axios set it with boundary
      config = {};
    } else {
      requestData = {
        name: houseHoldName,
        userDetails: selectedUsers,
        primaryContactId: primaryContact?.id || user?.id,
        primaryContactName: primaryContact?.name || user?.firstName + " " + user?.lastName,
      };
    }

    const request = selectedHousehold?.id
      ? AtriumApi.put(`/v2/households/${selectedHousehold?.id}`, requestData, config)
      : AtriumApi.post("/v2/households", requestData, config);

    request
      .then((response) => {
        if (response.status === 422) {
          notify.error(response.data.message);
        } else if (response.status === 201 || response.status === 200) {
          notify.success(selectedHousehold?.id ? "Household saved" : "Household created");
          setShowAddHousehold(false);
          setShowNewHouseholdForm(false);
          setIsEditingMember(false);
          getHouseHoldsByUser();
        } else {
          throw new Error("unexpected response");
        }
      })
      .catch((error) => {
        notify.error(getApiErrorMessage(error, "Could not save household"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteHousehold = async () => {
    const confirmed = await confirm({
      title: `Delete "${selectedHousehold?.name}"?`,
      message: "This household will be permanently deleted.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await AtriumApi.delete(`/v2/households/${selectedHousehold?.id}`);
      notify.success("Household deleted");
      setShowAddHousehold(false);
      setShowNewHouseholdForm(false);
      setIsEditingMember(false);
      getHouseHoldsByUser();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not delete household"));
    }
  };

  useEffect(() => {
    if (!selectedHousehold) {
      setSelectedUsers([
        {
          id: user?.id,
          name: user?.firstName + " " + user?.lastName,
          role: user?.role,
          avatar: { url: user?.avatar?.url },
          email: user?.email,
          phone: user?.phone,
          isPrimary: true,
          userDetails: {
            relationshipText: "",
            schoolGrade: "",
            allergies: [],
          },
        },
      ]);
    }
  }, [selectedHousehold]);

  const backButton = (onClick) => (
    <Button
      variant="ghost"
      size="sm"
      leftIcon={<MdArrowBack className="h-4 w-4" />}
      onClick={onClick}
      className="-ml-3 self-start"
    >
      Back
    </Button>
  );

  const selectedMemberIsPrimary =
    (!selectedUsers.some((u) => u.isPrimary) && selectedMember?.id === user?.id) ||
    selectedMember?.isPrimary;

  return (
    <div>
      {!showAddHousehold && !showNewHouseholdForm && !isEditingMember && (
        <div className="flex flex-col gap-4">
          <CardHeader
            title="Households"
            titleAs="h3"
            actions={
              <Button
                size="sm"
                leftIcon={<MdAdd className="h-4 w-4" />}
                disabled={user?.status !== "active"}
                onClick={() => {
                  setShowAddHousehold(true);
                  setSelectedHousehold(null);
                }}
              >
                Add Household
              </Button>
            }
          />

          {houseHoldsLsLoading ? (
            <div aria-busy="true" className="flex flex-col gap-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : houseHoldsError ? (
            <ErrorState
              message={getApiErrorMessage(houseHoldsError, "Households could not be loaded.")}
              onRetry={getHouseHoldsByUser}
            />
          ) : houseHoldsList?.length > 0 ? (
            <ul className="divide-y divide-line border-y border-line">
              {houseHoldsList.map((item) => (
                <li key={item?.id} className="flex items-center justify-between gap-3 py-3">
                  <span className="flex items-center gap-3 text-sm font-medium text-fg">
                    <MdHome aria-hidden="true" className="h-5 w-5 text-fg-muted" />
                    {item?.name}
                  </span>
                  <IconButton
                    icon={<MdEdit className="h-5 w-5" />}
                    label={`Edit ${item?.name}`}
                    size="sm"
                    onClick={() => {
                      setShowNewHouseholdForm(true);
                      setSelectedHousehold(item);
                      getUsersByHouseHold(item?.id);
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState variant="inline" title="No households yet" />
          )}
        </div>
      )}

      {showAddHousehold && !showNewHouseholdForm && !isEditingMember && (
        <div className="flex flex-col gap-5">
          {backButton(() => setShowAddHousehold(false))}

          <div>
            <p id={joinLabelId} className="mb-1.5 text-sm font-medium text-fg">
              Whose household would you like {user?.firstName} {user?.lastName} to join?
            </p>
            <HouseholdMultiSelect
              currentUser={user}
              users={usersList}
              selectedMembers={selectedUsers}
              setSelectedMembers={setSelectedUsers}
              aria-labelledby={joinLabelId}
            />
          </div>

          <Button
            fullWidth
            leftIcon={<MdHome className="h-4 w-4" />}
            onClick={() => setShowNewHouseholdForm(true)}
            disabled={selectedUsers?.length < 2}
          >
            {selectedHousehold ? "Update" : "Create"} Household with Selected Members
          </Button>
        </div>
      )}

      {showNewHouseholdForm && !isEditingMember && (
        <form className="flex flex-col gap-5" onSubmit={handleSaveHousehold}>
          <InputField
            label="Household Name"
            required
            placeholder="Household name"
            value={houseHoldName || ""}
            onChange={(e) => setHouseHoldName(e.target.value)}
          />
          <ImageUploader
            imagePreview={imagePreview}
            setImagePreview={setImagePreview}
            setImageFile={setHouseHoldImageFile}
          />

          <div className="rounded-card border border-line px-4">
            {usersLoading ? (
              <div aria-busy="true" className="flex flex-col gap-3 py-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : selectedUsers?.length > 0 ? (
              <ul className="divide-y divide-line">
                {selectedUsers.map((member) => {
                  const memberName =
                    member?.name || member?.firstName + " " + member?.lastName;
                  return (
                    <li key={member?.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <Avatar user={member} name={memberName} size="md" noBorder />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-fg">{memberName}</p>
                          <p className="truncate text-xs text-fg-muted">{member?.email}</p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {member?.isPrimary && (
                          <Badge tone="success" className="mr-1">
                            Primary
                          </Badge>
                        )}
                        <IconButton
                          icon={<MdEdit className="h-5 w-5" />}
                          label={`Edit ${memberName}`}
                          size="sm"
                          onClick={() => handleEditMember(member)}
                        />
                        {!member?.isPrimary && member?.id !== user?.id && (
                          <IconButton
                            icon={<MdDelete className="h-5 w-5" />}
                            label={`Remove ${memberName}`}
                            size="sm"
                            className="text-danger-fg"
                            onClick={() => handleRemoveUser({ ...member, name: memberName })}
                          />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState variant="inline" title="No people selected" />
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            {selectedHousehold && (
              <Button variant="danger" onClick={handleDeleteHousehold}>
                Delete Household
              </Button>
            )}
            <Button
              variant="secondary"
              leftIcon={<MdAdd className="h-4 w-4" />}
              onClick={() => {
                setShowNewHouseholdForm(false);
                setShowAddHousehold(true);
              }}
            >
              Add People
            </Button>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!houseHoldName || selectedUsers?.length < 2}
            >
              {loading ? "Saving…" : "Save Household"}
            </Button>
          </div>
        </form>
      )}

      {isEditingMember && selectedMember && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {backButton(() => setIsEditingMember(false))}
            <div className="flex items-center gap-2">
              {selectedMember?.id !== user?.id && !selectedMember?.isPrimary && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="text-danger-fg"
                  onClick={async () => {
                    if (await handleRemoveUser(selectedMember)) {
                      setIsEditingMember(false);
                    }
                  }}
                >
                  Remove
                </Button>
              )}
              {selectedMemberIsPrimary ? (
                <Badge tone="success">Primary</Badge>
              ) : (
                <Button variant="secondary" size="sm" onClick={handleMakePrimary}>
                  Make Primary
                </Button>
              )}
            </div>
          </div>

          <div className="grid gap-4">
            <InputField
              label="Relationship"
              placeholder="Relationship"
              value={selectedMember?.userDetails?.relationshipText || ""}
              onChange={(e) => handleUserDetailChange("relationshipText", e.target.value)}
            />
            <InputField
              label="School Grade"
              placeholder="School grade"
              value={selectedMember?.userDetails?.schoolGrade || ""}
              onChange={(e) => handleUserDetailChange("schoolGrade", e.target.value)}
            />
            {selectedMember?.userDetails?.allergies?.map((allergy, index) => (
              <div key={index} className="flex items-end gap-2">
                <InputField
                  label={`Allergy ${index + 1}`}
                  placeholder="Allergy"
                  value={allergy}
                  onChange={(e) => handleAllergyChange(index, e.target.value)}
                  className="flex-1"
                />
                <IconButton
                  icon={<MdClose className="h-5 w-5" />}
                  label={`Remove allergy ${index + 1}`}
                  onClick={() => handleRemoveAllergy(index)}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Button
              variant="secondary"
              leftIcon={<MdAdd className="h-4 w-4" />}
              onClick={handleAddAllergy}
              disabled={allergyFields?.includes("")}
            >
              Add Allergy
            </Button>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={() => setIsEditingMember(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HouseHold;
